import React, { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography, Tooltip } from '@mui/material';
import { companyInfo } from '../utils/dataInfo';
import { navbarLinks, getFilteredNavbarLinks, getCurrentUserType } from './navbarLinks';

const drawerWidth = 250;

// Color constants
const MAIN_LINK_BG = '#8CA9FF';
const CHILD_LINK_BG = '#f5f5f5';
const MAIN_LINK_BORDER = '#6E8FFF';
const CHILD_LINK_BORDER = '#A0D8EF';
const HOVER_BACKGROUND = 'rgba(140, 169, 255, 0.15)';
const THIRD_LEVEL_BG = '#e8f4ff';
const THIRD_LEVEL_BORDER = '#B3E0FF';

const DrawerRoot = styled('div')({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    minHeight: '64px',
    background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    borderBottom: `1px solid ${theme.palette.primary.light}30`,
    ...theme.mixins.toolbar,
    flexShrink: 0,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
            width: drawerWidth,
            height: '100vh',
            maxHeight: '100vh',
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            border: 'none',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
            overflowX: 'hidden',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            ...(!open && {
                width: theme.spacing(7),
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

const LogoSection = styled(Box)(({ theme, open }) => ({
    display: 'flex',
    height: '180px',
    alignItems: 'center',
    gap: theme.spacing(1),
    opacity: open ? 1 : 0,
    transition: 'all 0.3s ease',
    width: '100%',
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 0),
}));

const LogoContainer = styled(Box)(({ theme, open }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    height: open ? '40px' : '30px',
    width: '150px',
    marginBottom: open ? theme.spacing(0.5) : 0,
}));

const LogoImage = styled('img')(({ open }) => ({
    height: '175px',
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
    transition: 'all 0.3s ease',
    opacity: 1,
}));

const BrandText = styled(Typography)(({ theme, open }) => ({
    fontWeight: 700,
    fontSize: open ? '1.25rem' : '0',
    opacity: open ? 1 : 0,
    background: 'linear-gradient(45deg, #FFFFFF, #E3F2FD)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '100%',
    transition: 'all 0.3s ease',
    height: open ? 'auto' : 0,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.light + '20',
    '&:hover': {
        background: theme.palette.primary.light + '40',
    },
    transition: 'all 0.3s ease',
}));

const StyledListItem = styled(ListItem)(({ theme, selected, level = 0 }) => ({
    margin: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    paddingLeft: theme.spacing(1.5 + (level * 2)),
    transition: 'all 0.3s ease',
    border: selected ? `1px solid ${
        level === 0 ? MAIN_LINK_BORDER : 
        level === 1 ? CHILD_LINK_BORDER : 
        THIRD_LEVEL_BORDER
    }` : '1px solid transparent',
    background: selected
        ? level === 0 ? MAIN_LINK_BG : 
        level === 1 ? CHILD_LINK_BG : 
        THIRD_LEVEL_BG
        : 'transparent',
    '&:hover': {
        background: selected 
            ? level === 0 ? MAIN_LINK_BG : 
            level === 1 ? CHILD_LINK_BG : 
            THIRD_LEVEL_BG
            : HOVER_BACKGROUND,
        transform: selected ? 'none' : 'translateX(4px)',
        border: selected 
            ? `1px solid ${
                level === 0 ? MAIN_LINK_BORDER : 
                level === 1 ? CHILD_LINK_BORDER : 
                THIRD_LEVEL_BORDER
            }`
            : `1px solid ${MAIN_LINK_BORDER}30`,
        boxShadow: selected ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
    },
    '& .MuiListItemIcon-root': {
        minWidth: '40px',
        color: selected ? '#2C3E50' : '#f5f5f5',
    },
    '& .MuiListItemText-primary': {
        fontWeight: selected ? 700 : 500,
        fontSize: level === 0 ? '0.95rem' : 
                level === 1 ? '0.85rem' : '0.8rem',
        color: selected ? '#2C3E50' : '#f5f5f5',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const ParentListItem = styled(ListItem)(({ theme, selected, open, level = 0 }) => ({
    margin: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    paddingLeft: theme.spacing(1.5 + (level * 2)),
    transition: 'all 0.3s ease',
    border: selected || open ? `1px solid ${
        level === 0 ? MAIN_LINK_BORDER : 
        level === 1 ? CHILD_LINK_BORDER : 
        THIRD_LEVEL_BORDER
    }` : '1px solid transparent',
    background: selected || open
        ? level === 0 ? MAIN_LINK_BG : 
        level === 1 ? CHILD_LINK_BG : 
        THIRD_LEVEL_BG
        : 'transparent',
    '&:hover': {
        background: selected || open
            ? level === 0 ? MAIN_LINK_BG : 
            level === 1 ? CHILD_LINK_BG : 
            THIRD_LEVEL_BG
            : HOVER_BACKGROUND,
        transform: selected || open ? 'none' : 'translateX(4px)',
        border: selected || open
            ? `1px solid ${
                level === 0 ? MAIN_LINK_BORDER : 
                level === 1 ? CHILD_LINK_BORDER : 
                THIRD_LEVEL_BORDER
            }`
            : `1px solid ${MAIN_LINK_BORDER}30`,
        boxShadow: selected || open ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
    },
    '& .MuiListItemIcon-root': {
        minWidth: '40px',
        color: selected || open ? '#2C3E50' : '#f5f5f5',
    },
    '& .MuiListItemText-primary': {
        fontWeight: selected || open ? 700 : 500,
        fontSize: level === 0 ? '0.95rem' : 
                level === 1 ? '0.85rem' : '0.8rem',
        color: selected || open ? '#2C3E50' : '#f5f5f5',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const NestedList = styled(List)(({ theme }) => ({
    padding: 0,
    margin: 0,
}));

const MenuListContainer = styled(List)(({ theme }) => ({
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1, 0),
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.5)',
        },
    },
}));

export default function CustomDrawer({ open, toggleDrawer }) {
    const theme = useTheme();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState({});
    const [dynamicNavLinks, setDynamicNavLinks] = useState([]);
    const [currentUserType, setCurrentUserType] = useState('');

    // Load filtered navigation links based on user type
    useEffect(() => {
        const loadNavigation = () => {
            const userType = getCurrentUserType();
            setCurrentUserType(userType);
            const filteredLinks = getFilteredNavbarLinks();
            setDynamicNavLinks(filteredLinks);
            
            // Log for debugging
            console.log('Current User Type:', userType);
            console.log('Filtered Navigation Links:', filteredLinks.map(link => link.text));
        };
        
        loadNavigation();
        
        // Listen for storage events (in case user info changes in another tab)
        const handleStorageChange = (e) => {
            if (e.key === 'userInfo') {
                loadNavigation();
                // Reset expanded items when user changes
                setExpandedItems({});
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        // Also listen for custom event for same-tab updates
        const handleUserInfoUpdate = () => {
            loadNavigation();
            setExpandedItems({});
        };
        
        window.addEventListener('userInfoUpdate', handleUserInfoUpdate);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('userInfoUpdate', handleUserInfoUpdate);
        };
    }, []);

    const isItemSelected = (path) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const isParentSelected = (item) => {
        if (item.path) {
            return isItemSelected(item.path);
        }
        if (item.children) {
            return item.children.some(child => isParentSelected(child));
        }
        return false;
    };

    const handleToggle = (text) => {
        setExpandedItems(prev => ({
            ...prev,
            [text]: !prev[text]
        }));
    };

    const renderMenuItem = (item, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const selected = isItemSelected(item.path);
        const parentSelected = isParentSelected(item);

        if (hasChildren) {
            const isExpanded = expandedItems[item.text] || parentSelected;

            return (
                <React.Fragment key={item.text + level}>
                    <ParentListItem
                        button
                        onClick={() => handleToggle(item.text)}
                        selected={parentSelected}
                        open={isExpanded}
                        level={level}
                        sx={{
                            '& .MuiListItemText-secondary': {
                                display: open && level === 0 ? 'block' : 'none',
                                fontSize: '0.75rem',
                                opacity: parentSelected || isExpanded ? 0.9 : 0.7,
                                color: parentSelected || isExpanded ? '#2C3E50' : '#f5f5f5',
                                fontWeight: parentSelected || isExpanded ? 600 : 400,
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: parentSelected || isExpanded ? '#2C3E50' : '#f5f5f5',
                            fontSize: level === 0 ? '24px' : 
                                    level === 1 ? '22px' : '20px',
                        }}>
                            {open ? item.icon : (
                                <Tooltip title={item.text} placement="right" arrow>
                                    <span>{item.icon}</span>
                                </Tooltip>
                            )}
                        </ListItemIcon>
                        {open && (
                            <ListItemText 
                                primary={item.text}
                                secondary={level === 0 ? item.description : null}
                            />
                        )}
                        {open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                    </ParentListItem>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <NestedList>
                            {item.children.map(child => renderMenuItem(child, level + 1))}
                        </NestedList>
                    </Collapse>
                </React.Fragment>
            );
        }

        return (
            <StyledListItem
                key={item.text + level}
                button
                component={Link}
                to={item.path}
                selected={selected}
                level={level}
                sx={{
                    position: 'relative',
                    '&::before': selected ? {
                        content: '""',
                        position: 'absolute',
                        left: level === 0 ? '8px' : `${8 + (level * 16)}px`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '4px',
                        height: '70%',
                        backgroundColor: level === 0 ? MAIN_LINK_BORDER : 
                                    level === 1 ? CHILD_LINK_BORDER : 
                                    THIRD_LEVEL_BORDER,
                        borderRadius: '0 2px 2px 0',
                    } : {},
                    '& .MuiListItemText-secondary': {
                        display: open && level === 0 ? 'block' : 'none',
                        fontSize: '0.75rem',
                        opacity: selected ? 0.9 : 0.7,
                        color: selected ? '#2C3E50' : '#f5f5f5',
                        fontWeight: selected ? 600 : 400,
                    }
                }}
            >
                <ListItemIcon sx={{
                    color: selected ? '#2C3E50' : '#f5f5f5',
                    marginLeft: selected ? '4px' : '0',
                    fontSize: level === 0 ? '24px' : 
                                level === 1 ? '22px' : '20px',
                }}>
                    {open ? item.icon : (
                        <Tooltip title={item.text} placement="right" arrow>
                            <span>{item.icon}</span>
                        </Tooltip>
                    )}
                </ListItemIcon>
                {open && (
                    <ListItemText 
                        primary={item.text}
                        secondary={level === 0 ? item.description : null}
                    />
                )}
            </StyledListItem>
        );
    };

    // Show loading or empty state while checking permissions
    if (dynamicNavLinks.length === 0 && currentUserType === '') {
        return (
            <DrawerRoot>
                <Drawer variant="permanent" open={open}>
                    <DrawerHeader>
                        <LogoSection open={open}>
                            <LogoContainer open={open}>
                                {companyInfo.logo && (
                                    <LogoImage 
                                        src={companyInfo.logo} 
                                        alt={`${companyInfo.name} Logo`}
                                        open={open}
                                    />
                                )}
                            </LogoContainer>
                        </LogoSection>
                        <StyledIconButton onClick={toggleDrawer}>
                            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </StyledIconButton>
                    </DrawerHeader>
                    <Divider sx={{ borderColor: 'rgba(245, 245, 245, 0.3)', flexShrink: 0 }} />
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            Loading...
                        </Typography>
                    </Box>
                </Drawer>
            </DrawerRoot>
        );
    }

    return (
        <DrawerRoot>
            <Drawer variant="permanent" open={open}>
                <DrawerHeader>
                    <LogoSection open={open}>
                        {/* Company Logo Image */}
                        <LogoContainer open={open}>
                            {companyInfo.logo && (
                                <LogoImage 
                                    src={companyInfo.logo} 
                                    alt={`${companyInfo.name} Logo`}
                                    open={open}
                                />
                            )}
                        </LogoContainer>
                    </LogoSection>
                    <StyledIconButton onClick={toggleDrawer}>
                        {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </StyledIconButton>
                </DrawerHeader>

                <Divider sx={{ borderColor: 'rgba(245, 245, 245, 0.3)', flexShrink: 0 }} />

                <MenuListContainer>
                    {dynamicNavLinks.map((item) => renderMenuItem(item))}
                </MenuListContainer>
                
                {/* Optional: Display current user type for debugging (remove in production) */}
                {process.env.NODE_ENV === 'development' && (
                    <Box sx={{ p: 1, textAlign: 'center', borderTop: '1px solid rgba(245,245,245,0.3)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            User Type: {currentUserType.toUpperCase()}
                        </Typography>
                    </Box>
                )}
            </Drawer>
        </DrawerRoot>
    );
}